/* CompareScenarios — Phase G (strategic planning tool).
 *
 * Side-by-side view of the three scenarios already computed in
 * ForecastItaly's state machine: Optimistic / Base / Pessimistic. Each
 * scenario is a full row-array (computed by window.computeForecast)
 * with its own per-month eomTarget; this component summarizes each
 * into a single card answering "if this scenario plays out, what does
 * the year look like?"
 *
 * Today the three scenarios are already used as comparison lines on
 * the trajectory chart. This card surface adds a *narrative* view —
 * "to hit Optimistic, you need €9.5k/mo; Base €7.9k/mo; Pessimistic
 * €6.1k/mo" — without the user having to read scaled chart lines.
 *
 * Layout (3-column grid, stacks on narrow):
 *   ┌─────────────────┬─────────────────┬─────────────────┐
 *   │ 🚀 Optimistic  │ 📊 Base         │ 😟 Pessimistic │
 *   │ €342k Dec target│ €285k           │ €220k           │
 *   │ Need €9.5k/mo   │ Need €7.9k/mo   │ Need €6.1k/mo   │
 *   │ +50% vs base    │ (baseline)      │ −23% vs base    │
 *   └─────────────────┴─────────────────┴─────────────────┘
 *
 * Data flow: ForecastItaly already builds `compareScenarios` (base /
 * optimistic / pessimistic) via window.computeForecast at three
 * different scales. This component takes those three row-arrays plus
 * currentMrr/monthsLeft/lang/fmtMoney and renders the summary. No new
 * computation introduced; the math is shared with the chart's
 * comparison lines so the two surfaces always agree.
 */
import React from "react";
import Card from "../ui/Card";
import Section from "../ui/Section";

type ScenarioRow = {
  key: string;
  eomTarget: number;
};

interface CompareScenariosProps {
  /** Pre-computed row arrays from ForecastItaly's compareScenarios memo. */
  base: ScenarioRow[];
  optimistic: ScenarioRow[];
  pessimistic: ScenarioRow[];
  /** Current MRR — used to compute "gap" and "needed €/mo" per scenario. */
  currentMrr: number;
  /** Months remaining (unlocked rows). Drives the "/mo" pace number. */
  monthsLeft: number;
  /** "it" | "en" — drives all user-facing copy. */
  lang: "it" | "en";
  /** Money formatter — accepts a number, returns "€XYz" formatted string. */
  fmtMoney: (n: number) => string;
}

function getDecTarget(rows: ScenarioRow[]): number {
  return rows.find(r => r.key === "dec")?.eomTarget ?? 0;
}

interface ScenarioCardProps {
  emoji: string;
  label: string;
  decTarget: number;
  currentMrr: number;
  monthsLeft: number;
  baseDecTarget: number;
  isBase: boolean;
  lang: "it" | "en";
  fmtMoney: (n: number) => string;
  tone: "accent" | "neutral" | "warn";
}

function ScenarioCard({
  emoji,
  label,
  decTarget,
  currentMrr,
  monthsLeft,
  baseDecTarget,
  isBase,
  lang,
  fmtMoney,
  tone,
}: ScenarioCardProps) {
  const gap = Math.max(0, decTarget - currentMrr);
  const pace = monthsLeft > 0 ? gap / monthsLeft : 0;
  const deltaVsBase = baseDecTarget > 0 ? ((decTarget - baseDecTarget) / baseDecTarget) * 100 : 0;
  const paceLabel = lang === "it" ? `Serve ${fmtMoney(pace)}/mese` : `Need ${fmtMoney(pace)}/mo`;
  const deltaLabel = isBase
    ? (lang === "it" ? "(baseline)" : "(baseline)")
    : `${deltaVsBase > 0 ? "+" : ""}${deltaVsBase.toFixed(0)}% ${lang === "it" ? "vs base" : "vs base"}`;
  return (
    <Card padding="comfortable" elevation="raised" tone={tone} className={`scn-card scn-card--${isBase ? "base" : tone}`}>
      <div className="scn-card__head">
        <span className="scn-card__emoji" aria-hidden="true">{emoji}</span>
        <span className="scn-card__label">{label}</span>
      </div>
      <div className="scn-card__target">{fmtMoney(decTarget)}</div>
      <div className="scn-card__pace">{paceLabel}</div>
      <div className={`scn-card__delta scn-card__delta--${isBase ? "base" : deltaVsBase > 0 ? "up" : "down"}`}>
        {deltaLabel}
      </div>
    </Card>
  );
}

export default function CompareScenarios({
  base,
  optimistic,
  pessimistic,
  currentMrr,
  monthsLeft,
  lang,
  fmtMoney,
}: CompareScenariosProps) {
  const baseDec = getDecTarget(base);
  const optDec = getDecTarget(optimistic);
  const pessDec = getDecTarget(pessimistic);

  // Guard: if all three are zero (state not initialized), don't render at all.
  if (baseDec === 0 && optDec === 0 && pessDec === 0) return null;

  const title = lang === "it" ? "Scenari Dic 2026" : "Dec 2026 scenarios";

  return (
    <Section title={title} className="compare-scenarios">
      <div className="compare-scenarios__grid">
        <ScenarioCard
          emoji="🚀"
          label={lang === "it" ? "Ottimistico" : "Optimistic"}
          decTarget={optDec}
          currentMrr={currentMrr}
          monthsLeft={monthsLeft}
          baseDecTarget={baseDec}
          isBase={false}
          lang={lang}
          fmtMoney={fmtMoney}
          tone="accent"
        />
        <ScenarioCard
          emoji="📊"
          label={lang === "it" ? "Base" : "Base"}
          decTarget={baseDec}
          currentMrr={currentMrr}
          monthsLeft={monthsLeft}
          baseDecTarget={baseDec}
          isBase={true}
          lang={lang}
          fmtMoney={fmtMoney}
          tone="neutral"
        />
        <ScenarioCard
          emoji="😟"
          label={lang === "it" ? "Pessimistico" : "Pessimistic"}
          decTarget={pessDec}
          currentMrr={currentMrr}
          monthsLeft={monthsLeft}
          baseDecTarget={baseDec}
          isBase={false}
          lang={lang}
          fmtMoney={fmtMoney}
          tone="warn"
        />
      </div>
    </Section>
  );
}
